/* eslint-disable */
import React from "react";
import { useQuery } from "@apollo/client";
import { Alert, Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Tab, Tabs, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LaunchIcon from "@mui/icons-material/Launch";
import DOMPurify from "dompurify";
import { marked } from "marked";

import { PAGE_DIFF } from "../../gql/queries/crawlDiff";

/**
 * One page, three ways: what moved since the other report, how the page reads,
 * and the markdown exactly as it was stored.
 *
 * The diff is drawn the way a diff is normally read -- lines carry the
 * structure, and within a changed line the words that moved are marked. A
 * markdown paragraph is a single long line, so without that a one-word edit
 * repaints the whole paragraph and tells the reader nothing.
 *
 * Every page has content to show even when it has no diff, so an unchanged or
 * newly found page opens on the rendered view rather than on an empty panel.
 */

const DiffWrapper = styled(Box)(({ theme }) => ({
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: "12.5px",
  lineHeight: 1.7,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  // A grid, not flex. The line numbers take a fixed column and the text takes
  // `minmax(0, 1fr)`, which is what allows the text column to be narrower than
  // its own content -- a page holds urls like an image src with a signed hmac,
  // a single unbreakable token wider than the dialog. Under flex such a line
  // widens the row past the container and the text slides out from under its
  // own line numbers; a grid track simply cannot exceed its column.
  "& .hunk, & .row": {
    display: "grid",
    gridTemplateColumns: "84px minmax(0, 1fr)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  "& .hunk": {
    background: theme.palette.mode === "dark" ? "#1b2430" : "#eef1f5",
    color: theme.palette.text.secondary,
    padding: "6px 0",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  "& .hunk:first-of-type": { borderTop: "none" },
  "& .gutter": {
    textAlign: "right",
    paddingRight: "12px",
    color: theme.palette.text.disabled,
    userSelect: "none",
    fontVariantNumeric: "tabular-nums",
  },
  "& .text": { paddingRight: "12px", minWidth: 0 },
  "& .row.insert": { background: theme.palette.mode === "dark" ? "#12251a" : "#e9f5ed" },
  "& .row.insert .text": { color: theme.palette.mode === "dark" ? "#4ade80" : "#15803d" },
  "& .row.delete": { background: theme.palette.mode === "dark" ? "#2a1614" : "#fdecea" },
  "& .row.delete .text": { color: theme.palette.mode === "dark" ? "#f98a80" : "#b42318" },
  "& mark": {
    background: theme.palette.mode === "dark" ? "rgba(74,222,128,.22)" : "rgba(21,128,61,.16)",
    color: "inherit",
    fontWeight: 600,
    borderRadius: "2px",
    padding: "0 2px",
  },
  "& .row.delete mark": {
    background: theme.palette.mode === "dark" ? "rgba(249,138,128,.22)" : "rgba(180,35,24,.14)",
  },
}));

const SplitWrapper = styled(Box)(({ theme }) => ({
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: "12.5px",
  lineHeight: 1.7,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  "& .hunk": {
    background: theme.palette.mode === "dark" ? "#1b2430" : "#eef1f5",
    color: theme.palette.text.secondary,
    padding: "6px 12px",
    borderTop: `1px solid ${theme.palette.divider}`,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  "& .hunk:first-of-type": { borderTop: "none" },
  // Same reasoning as the unified view: fixed number column, text column that
  // is allowed to be narrower than its content.
  "& .row": { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", alignItems: "stretch" },
  "& .side": { display: "grid", gridTemplateColumns: "44px minmax(0, 1fr)", minWidth: 0 },
  "& .side + .side": { borderLeft: `1px solid ${theme.palette.divider}` },
  "& .num": {
    textAlign: "right",
    paddingRight: "10px",
    color: theme.palette.text.disabled,
    userSelect: "none",
    fontVariantNumeric: "tabular-nums",
  },
  "& .text": {
    padding: "0 10px",
    minWidth: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  "& .side.delete": { background: theme.palette.mode === "dark" ? "#2a1614" : "#fdecea" },
  "& .side.delete .text": { color: theme.palette.mode === "dark" ? "#f98a80" : "#b42318" },
  "& .side.insert": { background: theme.palette.mode === "dark" ? "#12251a" : "#e9f5ed" },
  "& .side.insert .text": { color: theme.palette.mode === "dark" ? "#4ade80" : "#15803d" },
  // A side with no counterpart is shown as absent rather than left blank, so
  // an added line cannot be mistaken for an edited one.
  "& .side.empty": { background: theme.palette.action.hover },
  "& mark": {
    background: theme.palette.mode === "dark" ? "rgba(74,222,128,.22)" : "rgba(21,128,61,.16)",
    color: "inherit",
    fontWeight: 600,
    borderRadius: "2px",
    padding: "0 2px",
  },
  "& .side.delete mark": {
    background: theme.palette.mode === "dark" ? "rgba(249,138,128,.22)" : "rgba(180,35,24,.14)",
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: "12.5px",
  lineHeight: 1.75,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  background: theme.palette.mode === "dark" ? "#101922" : "#f7f8fa",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  maxHeight: "60vh",
  overflowY: "auto",
}));

const RenderedWrapper = styled(Box)(({ theme }) => ({
  maxHeight: "60vh",
  overflowY: "auto",
  padding: theme.spacing(2, 3),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.background.paper,
  fontSize: "14px",
  lineHeight: 1.65,
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    lineHeight: 1.25,
    margin: theme.spacing(3, 0, 1),
    fontWeight: 600,
  },
  "& h1": { fontSize: "1.6rem" },
  "& h2": { fontSize: "1.3rem" },
  "& h3": { fontSize: "1.1rem" },
  "& h4, & h5, & h6": { fontSize: "1rem" },
  "& p": { margin: theme.spacing(0, 0, 1.5) },
  "& ul, & ol": { margin: theme.spacing(0, 0, 1.5), paddingLeft: theme.spacing(3) },
  "& li": { marginBottom: theme.spacing(0.5) },
  "& a": { color: theme.palette.primary.main, wordBreak: "break-word" },
  // Images come from the audited site and can be any size at all.
  "& img": { maxWidth: "100%", height: "auto", borderRadius: 4 },
  "& blockquote": {
    margin: theme.spacing(0, 0, 1.5),
    paddingLeft: theme.spacing(2),
    borderLeft: `3px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },
  "& code": {
    fontFamily: "ui-monospace, Menlo, Consolas, monospace",
    fontSize: "0.87em",
    background: theme.palette.action.hover,
    padding: "1px 5px",
    borderRadius: 3,
  },
  "& pre": { overflowX: "auto", background: theme.palette.action.hover, padding: theme.spacing(1.5), borderRadius: 4 },
  "& pre code": { background: "none", padding: 0 },
  // A wide table must scroll inside itself rather than stretch the dialog.
  "& .tableScroll": { overflowX: "auto", marginBottom: theme.spacing(1.5) },
  "& table": { borderCollapse: "collapse", width: "100%", fontSize: "13px" },
  "& th, & td": { border: `1px solid ${theme.palette.divider}`, padding: theme.spacing(0.75, 1), textAlign: "left" },
  "& th": { background: theme.palette.action.hover, fontWeight: 600 },
  "& hr": { border: "none", borderTop: `1px solid ${theme.palette.divider}`, margin: theme.spacing(2, 0) },
}));

const MetaRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));

/**
 * Markdown as html, with anything executable taken out of it.
 *
 * This text came off somebody else's website: markdownify passes through html
 * it does not convert, and marked passes that html straight to the output. A
 * script tag or an onerror attribute on a page we audited would otherwise run
 * inside this dashboard, so the result is sanitised before it is rendered --
 * the same DOMPurify pass the rest of the app uses. Links are sent to a new
 * tab, since they point at the audited site rather than anywhere in here.
 */
function renderMarkdown(markdown) {
  if (!markdown) return "";
  const openInNewTab = node => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  };
  DOMPurify.addHook("afterSanitizeAttributes", openInNewTab);
  try {
    return DOMPurify.sanitize(marked.parse(markdown), { ADD_ATTR: ["target"] });
  } finally {
    DOMPurify.removeHook("afterSanitizeAttributes");
  }
}

/**
 * The same lines, arranged as two columns.
 *
 * A removed run and the added run after it are the one thing a side-by-side
 * view exists for, so they are zipped: the first removed line faces the first
 * added line, and so on. When the runs are different lengths the shorter side
 * simply runs out, and those rows show one side as absent -- which is the
 * honest reading of a line that was added with nothing replaced.
 */
function toSplitRows(lines) {
  const rows = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i].op === "equal") {
      rows.push({ left: lines[i], right: lines[i] });
      i += 1;
      continue;
    }
    const removed = [];
    const added = [];
    while (i < lines.length && lines[i].op === "delete") removed.push(lines[i++]);
    while (i < lines.length && lines[i].op === "insert") added.push(lines[i++]);
    for (let k = 0; k < Math.max(removed.length, added.length); k += 1) {
      rows.push({ left: removed[k] || null, right: added[k] || null });
    }
  }
  return rows;
}

/** One half of a side-by-side row. */
function SplitSide({ line, side }) {
  if (!line) return <Box className="side empty" />;
  // An unchanged line is painted neither colour; a changed one takes the
  // colour of the side it sits on.
  const kind = line.op === "equal" ? "" : side === "left" ? "delete" : "insert";
  return (
    <Box className={`side ${kind}`}>
      <span className="num">{(side === "left" ? line.old : line.new) ?? ""}</span>
      <DiffText line={line} />
    </Box>
  );
}

/** A changed line, with the words that moved marked inside it. */
function DiffText({ line }) {
  if (!line.words || !line.words.length) return <span className="text">{line.text || " "}</span>;
  return (
    <span className="text">
      {line.words.map((w, i) => (w.op === "equal" ? <React.Fragment key={i}>{w.t}</React.Fragment> : <mark key={i}>{w.t}</mark>))}
    </span>
  );
}

const STATUS_NOTE = {
  same: "This page reads exactly as it did in the report you are comparing with.",
  new: "This page is not in the report you are comparing with.",
  removed:
    "The report you are comparing with audited this page and this one did not reach it. That can mean the page was taken down, or that this crawl stopped short of it — the counts above the list say which is more likely. What is shown here is the content the other report stored.",
  baseline: "There is nothing to compare this page against.",
  "not-audited": "Neither report stored content for this page.",
  rebaseline: "These two reports stored their pages in different ways, so the difference between them is not the site changing.",
};

const LAYOUT_KEY = "crawlDiff.layout";

/** The reader's last choice of layout, if the browser will tell us. */
function storedLayout() {
  try {
    return localStorage.getItem(LAYOUT_KEY) === "split" ? "split" : "unified";
  } catch {
    return "unified";
  }
}

function PageDiffDialog({ open, onClose, sessionId, compareTo, url }) {
  const [view, setView] = React.useState("changes");
  const [layout, setLayout] = React.useState(storedLayout);
  const [copied, setCopied] = React.useState(false);

  const { data, loading, error } = useQuery(PAGE_DIFF, {
    variables: { sessionId, url, compareTo },
    skip: !open || !url || !sessionId,
    fetchPolicy: "cache-first",
  });

  const diff = data?.PageDiff;
  const issues = diff?.issues;
  const hasDiff = !!diff?.hunks?.length;

  // A page with nothing to diff opens on its content instead of an empty
  // panel -- for an unchanged or newly found page the content is the whole of
  // what the reader came to see.
  React.useEffect(() => {
    if (!diff) return;
    setView(hasDiff ? "changes" : "rendered");
  }, [diff, hasDiff]);

  React.useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);

  const chooseLayout = next => {
    if (!next) return;                 // clicking the active button again
    setLayout(next);
    try {
      localStorage.setItem(LAYOUT_KEY, next);
    } catch {
      // A browser that refuses storage still gets the choice, just not next time.
    }
  };

  const copy = text => {
    if (!text) return;
    navigator.clipboard
      ?.writeText(text)
      .then(() => setCopied(true))
      .catch(() => {});
  };

  const rendered = React.useMemo(() => renderMarkdown(diff?.markdown), [diff?.markdown]);

  // The rendered HTML, read back as text. Derived from the markdown every
  // page already has, so it is always available -- unlike the page's
  // original source HTML, which this crawl may not have kept at all.
  const plainText = React.useMemo(() => {
    if (!rendered) return "";
    const el = document.createElement("div");
    el.innerHTML = rendered;
    return (el.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
  }, [rendered]);

  const recordJson = React.useMemo(
    () => (diff?.record ? JSON.stringify(diff.record, null, 2) : ""),
    [diff?.record]
  );

  const wordCount = diff?.markdown ? diff.markdown.split(/\s+/).filter(Boolean).length : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6, pb: 0 }}>
        <Typography component="div" sx={{ fontSize: "1rem", fontWeight: 600, wordBreak: "break-all" }}>
          {url}
          <Tooltip title="Open the page">
            <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ ml: 0.5 }}>
              <LaunchIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
        <Tabs value={view} onChange={(e, v) => setView(v)} sx={{ mt: 1, minHeight: 38 }}>
          <Tab value="changes" label={hasDiff ? `Changes (${diff.hunks.length})` : "Changes"} sx={{ minHeight: 38 }} />
          <Tab value="rendered" label="Rendered page" sx={{ minHeight: 38 }} />
          <Tab value="content" label="Markdown" sx={{ minHeight: 38 }} />
          <Tab value="text" label="Plain text" sx={{ minHeight: 38 }} />
          <Tab value="html" label="HTML" sx={{ minHeight: 38 }} />
          <Tab value="record" label="Record" sx={{ minHeight: 38 }} />
        </Tabs>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && <Alert severity="error">Could not load this page: {error.message}</Alert>}

        {!loading && !error && diff && (
          <>
            <MetaRow>
              <Chip
                size="small"
                label={diff.status}
                color={diff.status === "changed" ? "warning" : diff.status === "new" ? "info" : diff.status === "removed" ? "error" : "default"}
              />
              {diff.onlyVolatile && (
                <Tooltip title="Every section that moved here moves on every crawl — a counter, a timestamp, a rotating quote">
                  <Chip size="small" variant="outlined" label="only volatile sections" />
                </Tooltip>
              )}
              {!!wordCount && <Chip size="small" variant="outlined" label={`${wordCount.toLocaleString()} words stored`} />}
            </MetaRow>

            {STATUS_NOTE[diff.status] && (
              <Alert severity={diff.status === "rebaseline" || diff.status === "removed" ? "warning" : "info"} sx={{ mb: 2 }}>
                {diff.reason || STATUS_NOTE[diff.status]}
              </Alert>
            )}

            {view === "changes" && (
              <>
                {!!diff.fields?.length && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>Fields that changed</Typography>
                    {diff.fields.map((f, i) => (
                      <Box key={i} sx={{ mb: 1, fontSize: "13px" }}>
                        <Typography component="span" sx={{ fontWeight: 600, mr: 1 }}>
                          {f.Field}
                        </Typography>
                        <Box component="span" sx={{ color: "error.main", mr: 1 }}>
                          − {JSON.stringify(f.Previous)}
                        </Box>
                        <Box component="span" sx={{ color: "success.main" }}>
                          + {JSON.stringify(f.Current)}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {(!!issues?.gained?.length || !!issues?.lost?.length) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>Issues</Typography>
                    <MetaRow>
                      {(issues.gained || []).map(f => (
                        <Chip key={`g-${f}`} size="small" color="error" variant="outlined" label={`picked up ${f}`} />
                      ))}
                      {(issues.lost || []).map(f => (
                        <Chip key={`l-${f}`} size="small" color="success" variant="outlined" label={`fixed ${f}`} />
                      ))}
                    </MetaRow>
                  </Box>
                )}

                {hasDiff ? (
                  <>
                    <MetaRow sx={{ justifyContent: "flex-end" }}>
                      <ToggleButtonGroup size="small" exclusive value={layout} onChange={(e, v) => chooseLayout(v)}>
                        <ToggleButton value="unified" sx={{ textTransform: "none", px: 1.5 }}>
                          Unified
                        </ToggleButton>
                        <ToggleButton value="split" sx={{ textTransform: "none", px: 1.5 }}>
                          Split
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </MetaRow>

                    {layout === "unified" ? (
                      <DiffWrapper>
                        {diff.hunks.map((h, hi) => (
                          <Box key={hi}>
                            <Box className="hunk">
                              <span className="gutter" />
                              <span className="text">
                                {h.header}
                                {h.section ? `  ${h.section}` : ""}
                              </span>
                            </Box>
                            {h.lines.map((l, li) => (
                              <Box className={`row ${l.op}`} key={li}>
                                <span className="gutter">
                                  {`${l.old ?? ""}`.padStart(4)} {`${l.new ?? ""}`.padStart(4)}
                                </span>
                                <DiffText line={l} />
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </DiffWrapper>
                    ) : (
                      <SplitWrapper>
                        {diff.hunks.map((h, hi) => (
                          <Box key={hi}>
                            <Box className="hunk">
                              {h.header}
                              {h.section ? `  ${h.section}` : ""}
                            </Box>
                            {toSplitRows(h.lines).map((r, ri) => (
                              <Box className="row" key={ri}>
                                <SplitSide line={r.left} side="left" />
                                <SplitSide line={r.right} side="right" />
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </SplitWrapper>
                    )}
                  </>
                ) : (
                  <Typography sx={{ color: "text.secondary", py: 2 }}>
                    No line-by-line difference to show for this page. Its content is on the other tab.
                  </Typography>
                )}
              </>
            )}

            {view === "rendered" && (
              <>
                <MetaRow>
                  <Typography sx={{ fontWeight: 600 }}>
                    The page as it reads
                    {diff.markdownFrom && diff.markdownFrom !== sessionId ? ", from the report you are comparing with" : ""}
                  </Typography>
                </MetaRow>
                {diff.markdown ? (
                  <RenderedWrapper dangerouslySetInnerHTML={{ __html: rendered }} />
                ) : (
                  <Alert severity="info">No content was stored for this page.</Alert>
                )}
              </>
            )}

            {view === "content" && (
              <>
                <MetaRow sx={{ justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    The markdown as it was stored
                    {diff.markdownFrom && diff.markdownFrom !== sessionId ? ", from the report you are comparing with" : ""}
                  </Typography>
                  {!!diff.markdown && (
                    <Tooltip title={copied ? "Copied" : "Copy the markdown"}>
                      <IconButton size="small" onClick={() => copy(diff.markdown)}>
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </MetaRow>
                {diff.markdown ? (
                  <ContentWrapper>{diff.markdown}</ContentWrapper>
                ) : (
                  <Alert severity="info">No content was stored for this page.</Alert>
                )}
              </>
            )}

            {view === "text" && (
              <>
                <MetaRow sx={{ justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 600 }}>The page as plain text, with no markup at all</Typography>
                  {!!plainText && (
                    <Tooltip title={copied ? "Copied" : "Copy the text"}>
                      <IconButton size="small" onClick={() => copy(plainText)}>
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </MetaRow>
                {plainText ? (
                  <ContentWrapper>{plainText}</ContentWrapper>
                ) : (
                  <Alert severity="info">No content was stored for this page.</Alert>
                )}
              </>
            )}

            {view === "html" && (
              <>
                <MetaRow sx={{ justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    The HTML this crawl kept, rendered from its markdown -- not the page's original source
                  </Typography>
                  {!!rendered && (
                    <Tooltip title={copied ? "Copied" : "Copy the HTML"}>
                      <IconButton size="small" onClick={() => copy(rendered)}>
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </MetaRow>
                {rendered ? (
                  <ContentWrapper>{rendered}</ContentWrapper>
                ) : (
                  <Alert severity="info">No content was stored for this page.</Alert>
                )}
              </>
            )}

            {view === "record" && (
              <>
                <MetaRow sx={{ justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 600 }}>Everything this crawl kept for the page, as one record</Typography>
                  {!!recordJson && (
                    <Tooltip title={copied ? "Copied" : "Copy the record"}>
                      <IconButton size="small" onClick={() => copy(recordJson)}>
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </MetaRow>
                {recordJson ? (
                  <ContentWrapper>{recordJson}</ContentWrapper>
                ) : (
                  <Alert severity="info">This page was not stored by this crawl -- there is nothing to show a record for.</Alert>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PageDiffDialog;
